import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FlaskConical, Cpu, Lightbulb, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const iconMap = {
  FlaskConical,
  Cpu,
  Lightbulb,
};

const linkMap = {
  'arge': '/firsatlar#arge',
  'tech': '/firsatlar#teknoloji',
  'innovative': '/firsatlar#inovatif',
};

const OpportunitiesSection = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const opportunitiesConfig = [
    { id: 1, icon: 'FlaskConical', key: 'arge' },
    { id: 2, icon: 'Cpu', key: 'tech' },
    { id: 3, icon: 'Lightbulb', key: 'innovative' },
  ];

  const handleCardClick = (key) => {
    const link = linkMap[key];
    if (link) {
      const [path, hash] = link.split('#');
      navigate(path);
      setTimeout(() => {
        if (hash) {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } else {
          window.scrollTo(0, 0);
        }
      }, 100);
    }
  };

  const colors = [
    { border: '#4DFF88', icon: '#4DFF88', glow: 'from-[#4DFF88]/20' },
    { border: '#4DD6FF', icon: '#4DD6FF', glow: 'from-[#4DD6FF]/20' },
    { border: '#FF4DA6', icon: '#FF4DA6', glow: 'from-[#FF4DA6]/20' },
  ];

  return (
    <section id="opportunities" className="relative py-24 bg-[#060b1a]">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 right-1/4 w-80 h-80 bg-[#FF4DA6]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-[#4DFF88]/10 rounded-full blur-[180px]" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 rounded-full border border-[#FF4DA6]/30 bg-[#FF4DA6]/5 text-[#FF4DA6] text-sm font-medium mb-4">
            {t('opportunities.badge')}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white mb-4">
            {t('opportunities.title')}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('opportunities.subtitle')}
          </p>
        </div>

        {/* Opportunity Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {opportunitiesConfig.map((opportunity, index) => {
            const IconComponent = iconMap[opportunity.icon];
            const color = colors[index];
            const hasLink = linkMap[opportunity.key];

            return (
              <div
                key={opportunity.id}
                className={`group relative overflow-hidden ${hasLink ? 'cursor-pointer' : ''}`}
                onClick={() => hasLink && handleCardClick(opportunity.key)}
              >
                {/* Card */}
                <div
                  className="relative h-full p-8 rounded-xl border transition-all duration-500"
                  style={{
                    borderColor: `${color.border}20`,
                    background: `linear-gradient(135deg, ${color.border}08 0%, transparent 50%)`,
                  }}
                >
                  {/* Hover gradient overlay */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${color.glow} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`}
                  />

                  {/* Content */}
                  <div className="relative z-10">
                    {/* Icon */}
                    <div
                      className="mb-6 inline-flex items-center justify-center w-14 h-14 rounded-xl transition-transform duration-300 group-hover:scale-110"
                      style={{ backgroundColor: `${color.icon}15` }}
                    >
                      <IconComponent
                        className="w-7 h-7"
                        style={{ color: color.icon }}
                      />
                    </div>

                    {/* Title */}
                    <h3
                      className="text-xl font-semibold text-white mb-4 flex items-center gap-2 transition-colors duration-300"
                    >
                      {t(`opportunities.${opportunity.key}.title`)}
                      <ArrowUpRight
                        className="w-5 h-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                        style={{ color: color.icon }}
                      />
                    </h3>

                    {/* Description */}
                    <p className="text-gray-400 leading-relaxed">
                      {t(`opportunities.${opportunity.key}.description`)}
                    </p>
                  </div>

                  {/* Decorative corner */}
                  <div
                    className="absolute top-0 right-0 w-20 h-20 opacity-20 group-hover:opacity-40 transition-opacity duration-500"
                    style={{
                      background: `radial-gradient(circle at top right, ${color.icon}40 0%, transparent 70%)`,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OpportunitiesSection;
